import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import Icon from 'flarum/common/components/Icon';
import ItemList from 'flarum/common/utils/ItemList';
import Stream from 'flarum/common/utils/Stream';
import * as NickRules from '../utils/nickRules';
import { checkLogin, checkPassword, isEmail } from '../../common/accountRules';
import AgreementModal from './AgreementModal';
import SupportPanel from './SupportPanel';
import { enforceType, guardType } from '../components/AccountModals';
import { withEye } from '../components/PasswordEye';
const t = (key, params) => app.translator.trans('ppk2-profile.forum.signup.' + key, params);
const rt = (key, params) => app.translator.trans('ppk2-profile.lib.rules.' + key, params);
const M = 'flarum/forum/components/SignUpModal';

/** Server field (JSON:API pointer) -> form row. */
const ROWS = {
  username: 'username',
  ppk2MainNick: 'mainNick',
  email: 'email',
  password: 'password',
  ppk2AgreementAccepted: 'agreement'
};

/** One row in the profile-form style: label (left on desktop) | input + hint/status | info icon. */
function row(key, label, input, tipText, below) {
  return m("div", {
    className: 'Ppk2SignUp-row Ppk2SignUp-row--' + key
  }, m("label", {
    className: "Ppk2SignUp-label",
    for: 'ppk2-su-' + key
  }, label), m("div", {
    className: "Ppk2SignUp-body"
  }, input, below || null), m("div", {
    className: "Ppk2SignUp-info"
  }, tipText ? m("span", {
    className: "Ppk2SignUp-tip",
    tabindex: "0"
  }, m(Icon, {
    name: "fas fa-circle-info"
  }), m("span", {
    className: "Ppk2SignUp-tipText",
    role: "tooltip"
  }, tipText)) : null));
}
function statusLine(kind, text) {
  return m("div", {
    className: 'Ppk2SignUp-status Ppk2SignUp-status--' + kind
  }, m(Icon, {
    name: kind === 'ok' ? 'fas fa-circle-check' : kind === 'wait' ? 'fas fa-spinner fa-spin' : 'fas fa-circle-xmark'
  }), " ", text);
}
const hint = text => m("div", {
  className: "Ppk2SignUp-hint"
}, text);

/**
 * Password inputs of the form: always type=password, value NOT bound (a redraw would write the
 * stream back and wipe what a password manager filled in without an input event); the stream
 * follows the input / change events and is read from the DOM again before sending.
 */
function passwordAttrs(stream, after) {
  const sync = e => {
    enforceType(e.target, 'password');
    if (stream() !== e.target.value) {
      stream(e.target.value);
      if (after) after();
    } else e.redraw = false;
  };
  return {
    type: 'password',
    oncreate: vn => {
      vn.dom.value = stream() || '';
      guardType(vn.dom, 'password');
    },
    onupdate: vn => enforceType(vn.dom, 'password'),
    onremove: vn => vn.dom.__ppk2Guard && vn.dom.__ppk2Guard.disconnect(),
    onfocus: e => {
      enforceType(e.target, 'password');
      e.redraw = false;
    },
    oninput: sync,
    onchange: sync
  };
}
const tr = err => err ? app.translator.trans(err[0], err[1]) : null;

/**
 * Flarum's sign-up form in our profile-form style. Fields: «Логин для входа», «Основной игровой
 * ник» (live check), «Email», «Пароль», «Повторите пароль»; no «Псевдоним» (flarum/nicknames).
 * Each field: the rule as a one-line hint, the problem in red while typing; the server checks
 * the same rules. While the agreement is on, «Регистрация» stays disabled until the checkbox
 * «Я принимаю Пользовательское соглашение» is ticked.
 */
export default function extendSignUp() {
  extend(M, 'oninit', function () {
    this.ppk2 = {
      nick: Stream(this.attrs.ppk2MainNick || ''),
      repeat: Stream(''),
      support: null,
      agreed: false,
      lang: null,
      nickResult: null,
      nickWait: false,
      timer: null,
      tried: false,
      server: {}
    };
  });
  override(M, 'className', () => 'SignUpModal Ppk2SignUp');
  override(M, 'body', function () {
    const items = this.fields();
    if (items.has('nickname')) items.remove('nickname');
    return [m("div", {
      className: "Form Ppk2SignUp-form"
    }, items.toArray())];
  });
  override(M, 'fields', function () {
    const s = this.ppk2;
    const items = new ItemList();
    const active = app.forum.attribute('ppk2AgreementActive');
    const show = (key, value) => s.tried || value !== '' && value != null;
    const cls = (bad, ok) => 'FormControl' + (bad ? ' Ppk2SignUp-bad' : ok ? ' Ppk2SignUp-ok' : '');
    const serverLine = key => s.server[key] ? statusLine('bad', s.server[key]) : null;
    const clear = key => delete s.server[key];

    // «Логин для входа»
    const loginErr = this.isProvided('username') ? null : checkLogin(this.username());
    const loginBad = loginErr && show('username', this.username()) || s.server.username;
    items.add('username', row('username', t('username'), m("input", {
      id: "ppk2-su-username",
      className: cls(loginBad, !loginErr && this.username()),
      name: "username",
      type: "text",
      maxlength: "30",
      autocomplete: "username",
      spellcheck: "false",
      placeholder: t('username_ph'),
      value: this.username(),
      disabled: this.loading || this.isProvided('username'),
      oninput: e => (this.username(e.target.value), clear('username'))
    }), t('username_tip'), [hint(rt('login.hint')), loginErr && show('username', this.username()) ? statusLine('bad', tr(loginErr)) : serverLine('username')]), 60);

    // «Основной игровой ник»: our nick rule locally, then «занят / свободен» from the server
    const nick = NickRules.normalize(s.nick());
    const local = nick === null ? s.tried ? [NickRules.KEY + 'required', {}] : null : NickRules.check(nick);
    const result = s.nickResult && s.nickResult.nick === nick ? s.nickResult : null;
    let nickStatus = null;
    if (local) nickStatus = statusLine('bad', tr(local));else if (s.server.mainNick) nickStatus = serverLine('mainNick');else if (result) nickStatus = statusLine(result.status === 'free' ? 'ok' : 'bad', result.message);else if (s.nickWait && nick) nickStatus = statusLine('wait', t('nick_checking'));
    const taken = !local && !!result && result.status === 'taken';
    items.add('mainNick', row('mainNick', t('main_nick'), m("input", {
      id: "ppk2-su-mainNick",
      className: cls(local || s.server.mainNick || result && result.status !== 'free', result && result.status === 'free'),
      name: "ppk2MainNick",
      type: "text",
      maxlength: "32",
      autocomplete: "off",
      spellcheck: "false",
      placeholder: t('main_nick_ph'),
      value: s.nick(),
      disabled: this.loading,
      oninput: e => {
        s.nick(e.target.value);
        clear('mainNick');
        clearTimeout(s.timer);
        const n = NickRules.normalize(s.nick());
        s.nickWait = false;
        if (n === null || NickRules.check(n)) return;
        s.nickWait = true;
        s.timer = setTimeout(() => {
          app.request({
            method: 'POST',
            url: app.forum.attribute('apiUrl') + '/ppk2/nick-check',
            body: {
              nicks: [n]
            },
            errorHandler: () => {}
          }).then(r => s.nickResult = r.results[0], () => {}).finally(() => {
            s.nickWait = false;
            m.redraw();
          });
        }, 400);
      }
    }), t('main_nick_tip'), [hint(rt('nick_hint', {
      chars: m("span", {
        className: "nowrap"
      }, NickRules.SPECIAL_CHARS)
    })), nickStatus,
    // «Ник уже занят»: «Это ваш ник?» + «Написать в техподдержку» (inline, the form stays as it is)
    taken && s.support !== nick ? m("div", {
      className: "Ppk2SignUp-support"
    }, m("span", null, t('nick_support_line')), m(Button, {
      className: "Button Button--text Ppk2SignUp-supportBtn",
      type: "button",
      icon: "fas fa-headset",
      onclick: () => s.support = nick
    }, t('nick_support_button'))) : null, taken && s.support === nick ? m(SupportPanel, {
      nick: nick,
      email: this.email(),
      onclose: () => s.support = null
    }) : null]), 50);

    // «Email»: format checked while typing (the server checks again)
    const emailVal = String(this.email() || '').trim();
    const emailBad = !this.isProvided('email') && (emailVal !== '' && !isEmail(emailVal) || s.tried && emailVal === '');
    const emailOk = emailVal !== '' && isEmail(emailVal) && !s.server.email;
    items.add('email', row('email', t('email'), m("input", {
      id: "ppk2-su-email",
      className: cls(emailBad || s.server.email, emailOk),
      name: "email",
      type: "email",
      autocomplete: "email",
      placeholder: t('email_ph'),
      value: this.email(),
      disabled: this.loading || this.isProvided('email'),
      oninput: e => (this.email(e.target.value), clear('email')),
      onchange: e => this.email(e.target.value)
    }), t('email_tip'), emailBad ? statusLine('bad', this.email().trim() ? t('email_invalid') : app.translator.trans('ppk2-profile.lib.support.error_email')) : serverLine('email')), 40);
    if (!this.attrs.token) {
      const pwErr = checkPassword(this.password());
      const pwShow = show('password', this.password());
      items.add('password', row('password', t('password'), withEye(m("input", Object.assign({
        id: "ppk2-su-password",
        className: cls(pwErr && pwShow || s.server.password, !pwErr),
        name: "password",
        autocomplete: "new-password",
        placeholder: t('password_ph'),
        disabled: this.loading
      }, passwordAttrs(this.password, () => clear('password'))))), t('password_tip'), [hint(rt('password.hint')), pwErr && pwShow ? statusLine('bad', tr(pwErr)) : serverLine('password')]), 30);
      const mismatch = (s.repeat() || s.tried) && s.repeat() !== this.password();
      items.add('passwordRepeat', row('passwordRepeat', t('password_repeat'), withEye(m("input", Object.assign({
        id: "ppk2-su-passwordRepeat",
        className: cls(mismatch, s.repeat() && !mismatch && !pwErr),
        name: "passwordRepeat",
        autocomplete: "new-password",
        placeholder: t('password_repeat_ph'),
        disabled: this.loading
      }, passwordAttrs(s.repeat)))), null, mismatch ? statusLine('bad', t('password_mismatch')) : null), 20);
    }
    if (active) {
      items.add('agreement', m("div", {
        className: "Ppk2SignUp-agree"
      }, m("label", {
        className: "checkbox"
      }, m("input", {
        type: "checkbox",
        name: "ppk2AgreementAccepted",
        checked: s.agreed,
        onchange: e => (s.agreed = e.target.checked, clear('agreement')),
        disabled: this.loading
      }), m("span", null, t('agree_before'), m("a", {
        href: "#",
        onclick: e => {
          e.preventDefault();
          app.modal.show(AgreementModal, {
            lang: s.lang,
            onlang: code => s.lang = code
          }, true);
        }
      }, t('agree_link')))), serverLine('agreement')), 10);
    }
    items.add('submit', m("div", {
      className: "Form-group Ppk2SignUp-submit"
    }, m(Button, {
      className: "Button Button--primary Button--block",
      type: "submit",
      loading: this.loading,
      disabled: active && !s.agreed
    }, app.translator.trans('core.forum.sign_up.submit_button'))), -10);
    return items;
  });

  // our fields; never the flarum/nicknames «Псевдоним»
  extend(M, 'submitData', function (data) {
    const s = this.ppk2;
    delete data.nickname;
    if (!('username' in data)) data.username = this.username();
    data.ppk2MainNick = NickRules.normalize(s.nick());
    if (app.forum.attribute('ppk2AgreementActive')) {
      data.ppk2AgreementAccepted = s.agreed;
      if (s.lang) data.ppk2AgreementLang = s.lang;
    }
  });

  // local checks before core sends the form; the server repeats all of them
  override(M, 'onsubmit', function (original, e) {
    const s = this.ppk2;
    e.preventDefault();
    s.tried = true;
    // values a password manager put in without an input event
    const form = e.target && e.target.closest ? e.target.closest('form') || e.target : null;
    const dom = name => form && form.querySelector && form.querySelector('[name="' + name + '"]');
    if (dom('password') && dom('password').value !== this.password()) this.password(dom('password').value);
    if (dom('passwordRepeat') && dom('passwordRepeat').value !== s.repeat()) s.repeat(dom('passwordRepeat').value);
    if (app.forum.attribute('ppk2AgreementActive') && !s.agreed) return;
    const n = NickRules.normalize(s.nick());
    const bad = !this.isProvided('username') && checkLogin(this.username()) || n === null || NickRules.check(n) || s.nickResult && s.nickResult.nick === n && s.nickResult.status !== 'free' || !this.email() || !this.isProvided('email') && !isEmail(this.email()) || !this.attrs.token && (checkPassword(this.password()) || s.repeat() !== this.password());
    if (bad) {
      app.alerts.show({
        type: 'error'
      }, t('fix_errors'));
      return;
    }
    original(e);
  });

  // 422 from the server: messages under their fields (others: core alert)
  override(M, 'onerror', function (original, error) {
    const s = this.ppk2;
    const list = error && error.response && error.response.errors || [];
    let other = !list.length;
    s.server = {};
    list.forEach(err => {
      const field = (err.source && err.source.pointer || '').split('/').pop();
      if (ROWS[field] && error.status === 422) s.server[ROWS[field]] = err.detail;else other = true;
    });
    if (other) return original(error);
    app.alerts.show({
      type: 'error'
    }, t('fix_errors'));
    m.redraw();
  });
}
export { AgreementModal };
flarum.reg.add('ppk2-profile', 'forum/signup/signUp', extendSignUp);